import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useGoBack } from '@/lib/goBack';
import { isMessage, previewOf, useChats } from '@/data/store';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import { formatPhone } from '@/lib/contact';
import { Avatar } from '@/components/Avatar';
import { ArchiveIcon, BackIcon, CloseIcon, EditIcon, StarIcon, UnarchiveIcon } from '@/components/Icons';
import { Switch } from '@/components/Switch';
import { SheetOption, useOverlay } from '@/components/Overlay';
import { TeamInfoSheet } from '@/components/TeamInfoSheet';
import { canEditTeamInfo } from '@/lib/teamInfo';
import { TedTasks } from '@/components/TedTasks';
import { GroupCoach } from '@/components/GroupCoach';
import { setInfoTab, useInfoTab, useTedTasks } from '@/lib/tedTasks';

// A Team chat's Info, as a customer chat's (Gary, 15 Oct): the colleague's details from
// Sentinel (role, email, mobile, territories), starred messages, media, alerts and archive.

interface Facts { email: string | null; phone: string | null; role: string; territories: string[] }

export function TeamInfoScreen() {
  const { id } = useParams();
  const navigate = useNavigate();
  const goBack = useGoBack();
  const { team, live, setTeamMuted, setTeamArchived, removeTeamMember, setTeamAdmin, editTeamInfo } = useChats();
  const headOffice = useAuth().access.role === 'head_office';
  const { toast, openSheet, closeSheet } = useOverlay();
  const chat = team.find((t) => t.id === id);
  const personId = chat?.personId;
  const [facts, setFacts] = useState<Facts | null>(null);
  // A group Ted coaches opens on its Tasks tab (Gary, 1 Nov 2026); everything else is under Info.
  const coachable = live && chat?.kind === 'group';
  const { coaching } = useTedTasks(coachable ? chat.id : '', null, chat?.thread.length ?? 0);
  const infoTab = useInfoTab();

  useEffect(() => {
    if (!live || !personId) return;
    let gone = false;
    void (async () => {
      const [u, t] = await Promise.all([
        supabase.from('users').select('email, phone, permissions, is_pos_staff').eq('id', personId).maybeSingle(),
        supabase.from('user_territories').select('role, territories(name)').eq('user_id', personId),
      ]);
      if (gone) return;
      const p = u.data as { email: string | null; phone: string | null; permissions: Record<string, unknown> | null; is_pos_staff: boolean | null } | null;
      const scope = p?.permissions?._scope;
      const territories = ((t.data ?? []) as unknown as { role: string; territories: { name: string } | null }[])
        .map((r) => r.territories?.name).filter((n): n is string => !!n).sort();
      setFacts({
        email: p?.email ?? null,
        phone: p?.phone || null,
        role: scope === 'global' || scope === 'all' ? 'Head office' : p?.is_pos_staff ? 'Peace of Sleep team' : territories.length ? 'Operator' : 'Team member',
        territories,
      });
    })();
    return () => { gone = true; };
  }, [live, personId]);

  if (!chat) return <Navigate to="/team" replace />;
  const c = chat;
  const isGroup = c.kind === 'group' || c.kind === 'channel';
  const done = (msg: string) => (err: string | null) => toast(err ?? msg);
  // Name and description, the WhatsApp way (Gary, 17 Oct): group admins, head office for a channel.
  const canEdit = canEditTeamInfo(c, headOffice);

  function infoSheet() {
    if (c.kind !== 'group' && c.kind !== 'channel') return;
    openSheet(
      <TeamInfoSheet kind={c.kind} name={c.name} description={c.description ?? ''}
        onSave={async (name, description) => { if (await editTeamInfo(c.id, name, description)) closeSheet(); }} />,
    );
  }

  function memberSheet(m: { id: string; name: string; admin: boolean }) {
    openSheet(
      <>
        <h2 className="chats-sheet-title">{m.name}</h2>
        <SheetOption icon={<Avatar name={m.name} colour={m.name.length} small />} title={m.admin ? 'Remove as admin' : 'Make admin'} sub={m.admin ? 'They can no longer add or remove people' : 'They can add and remove people too'}
          onClick={() => { closeSheet(); void setTeamAdmin(c.id, m.id, !m.admin).then(done(m.admin ? 'No longer an admin' : 'Now an admin')); }} />
        <SheetOption icon={<CloseIcon />} title="Remove from group" sub="They stop seeing new messages" danger
          onClick={() => removeSheet(m)} />
      </>,
    );
  }

  // Remove and Leave ask first, as Delete message does (mobile review, 19 Oct: one slip of the thumb acted at once).
  function removeSheet(m: { id: string; name: string }) {
    openSheet(
      <>
        <h2 className="chats-sheet-title">{`Remove ${m.name}?`}</h2>
        <p className="chats-sheet-hint">They stop seeing new messages in {c.name}. An admin can add them back.</p>
        <div className="chats-pair">
          <button type="button" className="chats-btn chats-btn--ghost" onClick={closeSheet}>Cancel</button>
          <button type="button" className="chats-btn chats-btn--danger" onClick={() => { closeSheet(); void removeTeamMember(c.id, m.id).then(done(`${m.name} removed`)); }}>Remove</button>
        </div>
      </>,
    );
  }

  function leaveSheet() {
    const me = (c.members ?? []).find((m) => m.name === 'You')?.id ?? '';
    openSheet(
      <>
        <h2 className="chats-sheet-title">{`Leave ${c.name}?`}</h2>
        <p className="chats-sheet-hint">You stop seeing its messages. Only an admin can add you back.</p>
        <div className="chats-pair">
          <button type="button" className="chats-btn chats-btn--ghost" onClick={closeSheet}>Cancel</button>
          <button type="button" className="chats-btn chats-btn--danger" onClick={() => {
            closeSheet();
            void removeTeamMember(c.id, me).then((err) => {
              if (err) { toast(err); return; }
              toast('You left the group');
              // Back past this Info and the group's chat to where the group was opened from, so Back never
              // returns to the group just left (regression run, 19 Oct). Opened straight on this Info or
              // the chat (a link or an alert): the Team list in place of it. After the closing sheet's
              // own history step has gone, or the step would be counted from that one instead.
              const toTeam = () => {
                const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
                if (idx >= 2) navigate(-2);
                else navigate('/team', { replace: true });
              };
              if ((window.history.state as Record<string, unknown> | null)?.chatsSheet) window.addEventListener('popstate', toTeam, { once: true });
              else toTeam();
            });
          }}>Leave</button>
        </div>
      </>,
    );
  }
  const coached = coachable && coaching === true;
  const tasksTab = coached && infoTab === 'tasks';
  const messages = c.thread.filter(isMessage);
  const starred = messages.filter((m) => m.starred);
  const photos = messages.filter((m) => m.body.kind === 'photo').length;
  const docs = messages.filter((m) => m.body.kind === 'link').length;

  return (
    <div className="chats-conv chats-contact">
      <header className="chats-topbar chats-topbar--conv">
        <button type="button" className="chats-icon-btn chats-back" aria-label="Back" onClick={() => goBack(`/team/${c.id}`)}><BackIcon /></button>
        <h1 className="chats-topbar-title">{tasksTab ? 'Tasks' : 'Info'}</h1>
      </header>
      {coached && (
        <div className="chats-channel-tabs" role="tablist" aria-label="Info">
          {(['tasks', 'info'] as const).map((t) => (
            <button key={t} type="button" role="tab" aria-selected={infoTab === t}
              className={`chats-channel-tab chats-channel-tab--whatsapp${infoTab === t ? ' active' : ''}`}
              onClick={() => setInfoTab(t)}>{t === 'tasks' ? 'Tasks' : 'Info'}</button>
          ))}
        </div>
      )}
      <main className="chats-screen chats-screen--grey">
        {tasksTab && <div className="chats-task-pane"><TedTasks threadId={c.id} bump={c.thread.length} /></div>}
        {!tasksTab && <>
        <div className="chats-contact-hero">
          <Avatar name={c.name} colour={c.colour} />
          {canEdit ? (
            <button type="button" className="chats-contact-rename" aria-label={`Edit ${c.kind === 'channel' ? 'channel' : 'group'} name and description`} onClick={infoSheet}>
              <h2>{c.name}</h2>
              <EditIcon />
            </button>
          ) : <h2>{c.name}</h2>}
          {facts?.phone && <p className="chats-contact-phone">{formatPhone(facts.phone)}</p>}
          {isGroup && c.description && <p className="chats-contact-about">{c.description}</p>}
          {canEdit && !c.description && (
            <button type="button" className="chats-contact-about-add" onClick={infoSheet}>{`Add ${c.kind === 'channel' ? 'channel' : 'group'} description`}</button>
          )}
        </div>

        {isGroup && (
          <>
            <p className="chats-group-title">{c.kind === 'channel' ? 'Channel' : 'Group'} · {c.members?.length ?? 0} members</p>
            <div className="chats-group">
              {/* First in the list, as WhatsApp: group admins, and head office for a channel. */}
              {!c.system && (c.amAdmin || (c.kind === 'channel' && headOffice)) && (
                <button type="button" className="chats-set" onClick={() => navigate(`/team/${c.id}/add`)}>
                  <span className="chats-set-add" aria-hidden="true">+</span>
                  <div className="chats-set-main"><b>Add people</b></div>
                </button>
              )}
              {c.kind === 'channel' && (
                <div className="chats-set"><div className="chats-set-main"><small>{c.system
                  ? 'Head office only. Sentinel posts platform alerts here; you read, react and can mute it.'
                  : 'Everyone on the team is in it. Head office posts; everyone else reads, reacts and can mute it.'}</small></div></div>
              )}
              {(c.members ?? []).map((m) => (
                <button key={m.id} type="button" className="chats-set" disabled={!c.amAdmin || c.kind === 'channel' || m.name === 'You'} onClick={() => memberSheet(m)}>
                  <Avatar name={m.name} colour={m.name.length} small />
                  <div className="chats-set-main">{m.name}</div>
                  {m.admin && <span className="chats-set-ok">Admin</span>}
                </button>
              ))}
              {c.kind === 'group' && (
                <button type="button" className="chats-set chats-set--danger" onClick={leaveSheet}>
                  <div className="chats-set-main">Leave group</div>
                </button>
              )}
            </div>
          </>
        )}

        {live && headOffice && c.kind === 'group' && <GroupCoach chat={c} />}

        {!isGroup && <p className="chats-group-title">Colleague</p>}
        {!isGroup && <div className="chats-group chats-facts">
          <div className="chats-facts-pair"><span>Role</span><b>{facts?.role ?? '...'}</b></div>
          {facts?.email && <div className="chats-facts-pair"><span>Email</span><b>{facts.email}</b></div>}
          {facts && facts.territories.length > 0 && <div className="chats-facts-pair"><span>Territories</span><b>{facts.territories.join(', ')}</b></div>}
          <div className="chats-facts-pair"><span>Chat</span><b>Team only, never WhatsApp</b></div>
        </div>}

        <p className="chats-group-title">Media and docs</p>
        <div className="chats-group">
          <div className="chats-set"><div className="chats-set-main">Photos<small>{photos ? `${photos} in this chat` : 'None yet'}</small></div></div>
          <div className="chats-set"><div className="chats-set-main">Documents<small>{docs ? `${docs} in this chat` : 'None yet'}</small></div></div>
        </div>

        <p className="chats-group-title">Starred messages</p>
        <div className="chats-group">
          {starred.length === 0 && <div className="chats-set"><div className="chats-set-main"><small>Hold a message and tap Star to keep it here.</small></div></div>}
          {starred.map((m) => (
            <button key={m.id} type="button" className="chats-set" onClick={() => navigate(`/team/${c.id}?m=${encodeURIComponent(m.id)}`)}>
              <span className="chats-row-flag"><StarIcon filled /></span>
              <div className="chats-set-main">{previewOf(m.body)}<small>{m.at}</small></div>
            </button>
          ))}
        </div>

        <div className="chats-group">
          <button type="button" className="chats-set" onClick={() => { setTeamMuted(c.id, !c.muted); toast(c.muted ? 'Alerts on for this chat' : 'Alerts off for this chat'); }}>
            <div className="chats-set-main">
              Alerts for this chat
              <small>{c.muted ? 'Off: no alerts from this chat. New messages still show as unread.' : 'On: your phone alerts you to every new message.'}</small>
            </div>
            <Switch on={!c.muted} label="Alerts for this chat" />
          </button>
          {/* Not for a channel: it always lists under Channels, so archiving changed nothing but the badge
              (mobile review, 19 Oct). Alerts covers quietening one. A channel archived before stays unarchivable. */}
          {(c.kind !== 'channel' || c.archived) && (
            <button type="button" className="chats-set" onClick={() => { setTeamArchived(c.id, !c.archived); toast(c.archived ? 'Chat unarchived' : 'Chat archived'); navigate(c.archived ? `/team/${c.id}` : '/team'); }}>
              <span className="chats-row-flag">{c.archived ? <UnarchiveIcon /> : <ArchiveIcon />}</span>
              <div className="chats-set-main">{c.archived ? 'Unarchive chat' : 'Archive chat'}<small>{c.kind === 'group' ? (c.archived ? 'Back to your Groups list.' : 'Off your Groups list; still under Groups, Archived.') : c.kind === 'channel' ? 'Back to a normal channel.' : 'Off your Chats tab; still under All.'}</small></div>
            </button>
          )}
        </div>
        </>}
      </main>
    </div>
  );
}
